/**
 * Admin · Bookings & consultant configuration card on the Account page.
 * Owns the booking email, timezone, lead/horizon, and weekly availability builder.
 */
import { Settings, Save } from "lucide-react";
import { safeTz } from "@/lib/timezone";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function AdminSettingsCard({ form, setForm, saving, onSave }) {
  const tzInvalid = form.booking_timezone && safeTz(form.booking_timezone, null) === null;

  const addAvailability = () =>
    setForm((s) => ({
      ...s,
      booking_availability: [
        ...(s.booking_availability || []),
        { _uid: `av_${Math.random().toString(36).slice(2, 10)}`, weekday: 1, start: "10:00", end: "12:00" },
      ],
    }));

  const updateAvailability = (uid, patch) =>
    setForm((s) => ({
      ...s,
      booking_availability: s.booking_availability.map((r) => (r._uid === uid ? { ...r, ...patch } : r)),
    }));

  const removeAvailability = (uid) =>
    setForm((s) => ({
      ...s,
      booking_availability: s.booking_availability.filter((r) => r._uid !== uid),
    }));

  return (
    <div data-testid="admin-settings-card" className="mt-10 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
      <div className="flex items-center gap-2">
        <Settings className="h-4 w-4 text-teal-700 dark:text-teal-400" />
        <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Admin · Bookings &amp; consultant</p>
      </div>
      <h3 className="mt-2 font-display text-lg font-semibold text-slate-900 dark:text-slate-50">Booking configuration</h3>
      <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
        Bookings appear in the &quot;Book a continuity consultant&quot; modal across the app. Set the email that receives requests and your weekly availability — visitors will only see slots that are open.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Lead consultant email</label>
          <input
            value={form.consultant_email}
            onChange={(e) => setForm((s) => ({ ...s, consultant_email: e.target.value }))}
            placeholder="lead@example.com"
            data-testid="admin-consultant-email"
            className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Booking email (where requests go)</label>
          <input
            value={form.booking_email}
            onChange={(e) => setForm((s) => ({ ...s, booking_email: e.target.value }))}
            placeholder="(defaults to lead consultant email)"
            data-testid="admin-booking-email"
            className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Your timezone (IANA)</label>
          <input
            value={form.booking_timezone}
            onChange={(e) => setForm((s) => ({ ...s, booking_timezone: e.target.value }))}
            placeholder="Europe/London"
            data-testid="admin-booking-tz"
            className={`mt-1.5 w-full h-10 px-3 rounded-md border bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 ${
              tzInvalid
                ? "border-amber-500 focus:ring-amber-500/30 focus:border-amber-600"
                : "border-slate-200 dark:border-slate-700 focus:ring-teal-500/30 focus:border-teal-600"
            }`}
          />
          {tzInvalid ? (
            <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-400">
              &ldquo;{form.booking_timezone}&rdquo; isn&apos;t a valid IANA timezone. Try <b>Europe/London</b>, <b>America/Jamaica</b>, <b>America/New_York</b>, or <b>Asia/Singapore</b>.
            </p>
          ) : (
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">e.g. Europe/London, America/Jamaica, America/New_York, Asia/Singapore</p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Lead time (hrs)</label>
            <input
              type="number" min="0"
              value={form.booking_lead_hours}
              onChange={(e) => setForm((s) => ({ ...s, booking_lead_hours: parseInt(e.target.value || "0", 10) }))}
              data-testid="admin-booking-lead"
              className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Horizon (days)</label>
            <input
              type="number" min="1"
              value={form.booking_horizon_days}
              onChange={(e) => setForm((s) => ({ ...s, booking_horizon_days: parseInt(e.target.value || "1", 10) }))}
              data-testid="admin-booking-horizon"
              className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
            />
          </div>
        </div>
      </div>

      {/* Availability builder */}
      <div className="mt-7">
        <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Weekly availability</p>
        <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">30-minute slots are generated inside each window.</p>

        <div className="mt-3 space-y-2">
          {(form.booking_availability || []).map((rule, idx) => (
            <div key={rule._uid || `av-${idx}`} data-testid={`admin-avail-row-${idx}`} className="flex flex-wrap items-end gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900 p-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Day</label>
                <select
                  value={rule.weekday}
                  onChange={(e) => updateAvailability(rule._uid, { weekday: parseInt(e.target.value, 10) })}
                  data-testid={`admin-avail-day-${idx}`}
                  className="mt-1 h-9 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
                >
                  {WEEKDAYS.map((d, i) => (
                    <option key={d} value={i}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Start</label>
                <input
                  type="time"
                  value={rule.start}
                  onChange={(e) => updateAvailability(rule._uid, { start: e.target.value })}
                  data-testid={`admin-avail-start-${idx}`}
                  className="mt-1 h-9 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">End</label>
                <input
                  type="time"
                  value={rule.end}
                  onChange={(e) => updateAvailability(rule._uid, { end: e.target.value })}
                  data-testid={`admin-avail-end-${idx}`}
                  className="mt-1 h-9 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
                />
              </div>
              <button
                onClick={() => removeAvailability(rule._uid)}
                data-testid={`admin-avail-remove-${idx}`}
                className="ml-auto h-9 px-3 rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-xs hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Remove
              </button>
            </div>
          ))}
          {(!form.booking_availability || form.booking_availability.length === 0) && (
            <p className="text-xs text-slate-500 dark:text-slate-400 italic">No availability set — bookings are closed.</p>
          )}
        </div>
        <button
          onClick={addAvailability}
          data-testid="admin-avail-add"
          className="mt-3 inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-xs hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          + Add availability window
        </button>
      </div>

      <div className="mt-7 pt-6 border-t border-slate-200 dark:border-slate-800">
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <button
            type="button"
            role="switch"
            aria-checked={!!form.show_advisors}
            onClick={() => setForm((s) => ({ ...s, show_advisors: !s.show_advisors }))}
            data-testid="admin-show-advisors-toggle"
            className={`mt-0.5 relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${form.show_advisors ? "bg-teal-600" : "bg-slate-300 dark:bg-slate-700"}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${form.show_advisors ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
          <span>
            <span className="block text-sm font-medium text-slate-900 dark:text-slate-50">Show &ldquo;For advisors&rdquo; link in the menu</span>
            <span className="block mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">When off, the public /advisors nav link is hidden (desktop &amp; mobile). The page itself stays reachable by direct URL.</span>
          </span>
        </label>

        <label className="mt-6 block text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Advisor toolkit link (partner app)</label>
        <input
          value={form.advisor_toolkit_url || ""}
          onChange={(e) => setForm((s) => ({ ...s, advisor_toolkit_url: e.target.value }))}
          placeholder="https://your-partner-app.com/subscribe"
          data-testid="admin-advisor-toolkit-url"
          className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
        />
        <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
          Where the &ldquo;Get the Advisor Toolkit&rdquo; button on the /advisors page sends advisors to subscribe. Leave blank to hide the button.
        </p>
      </div>

      <button
        onClick={onSave}
        disabled={saving}
        data-testid="admin-settings-save"
        className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors disabled:opacity-50"
      >
        <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save settings"}
      </button>
    </div>
  );
}
